import * as React from 'react';
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { usePageTracking } from '../../tracking';
import { solveFold, constrainCorner, cornerPoint, polygonCss, matrixCss } from './geometry';
import { PageOverlays, extractPdfLinks } from './overlays';
import './book.css';
import { onPdfLoadError } from '../ReloadNotice';

// Realistic flipbook: two-page spreads with a draggable page curl, single pages
// on narrow screens. Hand-rolled (no flip library) so page content stays real
// DOM — pdf canvas plus an overlay layer for links/videos (see overlays.jsx).
//
// Rendering model: every page within a small window around the current spread
// is mounted once, keyed by page number, and never moved between parents (that
// would re-rasterize the canvas). Each animation frame, applyLayout() assigns
// roles by writing transform / clip-path / z-index / visibility straight to the
// DOM. React only decides *which* pages are mounted; it never sets those styles.

pdfjs.GlobalWorkerOptions.workerSrc =
    process.env.NODE_ENV === 'production'
        ? 'https://pdflink.to/static/frontend/pdf.worker.min.js'
        : '/pdf.worker.min.js';

const IS_TOUCH = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
const CANVAS_DPR =
    IS_TOUCH && typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : undefined;

// A drag only commits to a direction once it has moved this far sideways and
// more sideways than vertically — the first few px of a swipe are often a wobble.
const DRAG_START_PX = 10;
const VELOCITY_WINDOW_MS = 80;
// A released drag completes past this fraction of the turn, or when flicked
// faster than FLICK px/ms. Single-page mode is a phone swipe across one page
// rather than a drag across a spread, so it needs less travel.
const COMPLETE_AT = { spread: 0.3, single: 0.08 };
const FLICK = { spread: 0.45, single: 0.15 };
// A quick swipe (short press, moved this far in the turning direction) always turns.
const SWIPE_MS = 500;
const SWIPE_PX = 24;
const MAX_ZOOM = 4;
// Inside a frame, a drag that goes quiet this long near the edge has left it.
const FRAME_IDLE_MS = 250;
// ?fbdebug=1 logs every release decision to the console (for tuning on real devices).
const FB_DEBUG = (() => { try { return new URLSearchParams(window.location.search).get('fbdebug') === '1'; } catch (err) { return false; } })();
const IN_FRAME = (() => { try { return window.self !== window.top; } catch (err) { return true; } })();
// Canvas pixel budget for the extra-sharp layer drawn while zoomed. iOS caps a
// single canvas at ~16.7M px and kills tabs that hoard canvas memory.
const HIRES_MAX_PIXELS = IS_TOUCH ? 8e6 : 16e6;
const Z = { base: 0, page: 1, underShadow: 2, front: 3, frontShade: 4, drop: 5, back: 6, backShade: 7 };

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const bezier = (a, c, b, t) => ({
    x: (1 - t) * (1 - t) * a.x + 2 * (1 - t) * t * c.x + t * t * b.x,
    y: (1 - t) * (1 - t) * a.y + 2 * (1 - t) * t * c.y + t * t * b.y,
});

// --- Layout ------------------------------------------------------------------

// mode: 'auto' picks whichever of one page / two-page spread shows pages
// biggest; 'single' and 'double' force it (set per document, or toggled by the
// viewer). A phone held upright always gets one page — a spread would be unreadable.
export const canShowSpread = (vw, vh) => vw >= 600 || vw > vh;

const computeLayout = (vw, vh, aspect, mode) => {
    const padX = vw < 600 ? 12 : 64;
    // Short screens (phones in landscape) hand almost all height to the book;
    // book.css shrinks the bar and pulls the nav pill down to match.
    // The top bar (book.css .fb-bar) is 56px, 48px on short screens.
    const padTop = vh < 500 ? 56 : vh < 600 ? 64 : 80;
    const padBottom = vh < 500 ? 60 : vh < 600 ? 72 : 96;
    const availW = Math.max(100, vw - padX * 2);
    const availH = Math.max(100, vh - padTop - padBottom);
    const single = Math.min(availW, availH * aspect);
    const double = Math.min(availW / 2, availH * aspect);
    let spread;
    if (mode === 'single') spread = false;
    else if (mode === 'double') spread = canShowSpread(vw, vh);
    else spread = vw >= 700 && double >= single * 0.75;
    const W = Math.floor(spread ? double : single);
    return { spread, W, H: Math.round(W / aspect), offsetY: (padTop - padBottom) / 2 };
};

// Which pages are on screen when not turning. Spreads follow print convention:
// the cover sits alone on the right, then 2|3, 4|5, ...
const visiblePages = (spread, page, n) => {
    if (!spread) return { left: null, right: page };
    const k = Math.floor(page / 2);
    return { left: k * 2 >= 1 ? k * 2 : null, right: k * 2 + 1 <= n ? k * 2 + 1 : null };
};

// Work out the sheets involved in turning forward ('next') or back ('prev').
// dir is the geometric direction (+1 = the sheet travels right-to-left).
// reverse means the sheet starts fully turned and comes back to rest — how a
// single-page view shows "previous": the earlier page is laid back on top.
const planFlip = (spread, page, n, which, corner) => {
    if (spread) {
        const k = Math.floor(page / 2);
        if (which === 'next') {
            if (2 * k + 2 > n) return null;
            return { dir: 1, corner, front: 2 * k + 1, back: 2 * k + 2, under: 2 * k + 3 <= n ? 2 * k + 3 : null, other: k > 0 ? 2 * k : null, target: 2 * k + 2, reverse: false };
        }
        if (k < 1) return null;
        return { dir: -1, corner, front: 2 * k, back: 2 * k - 1, under: 2 * k - 2 >= 1 ? 2 * k - 2 : null, other: 2 * k + 1 <= n ? 2 * k + 1 : null, target: 2 * k - 1, reverse: false };
    }
    if (which === 'next') {
        if (page >= n) return null;
        return { dir: 1, corner, front: page, back: 'paper', under: page + 1, other: null, target: page + 1, reverse: false };
    }
    if (page <= 1) return null;
    return { dir: 1, corner, front: page - 1, back: 'paper', under: page, other: null, target: page - 1, reverse: true };
};

const smoothstep = (a, b, x) => {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
};

// The back of a single sheet (one-page mode) shows its own page faintly and
// mirrored, the way a real page shows through when you lift it, so the turning
// sheet reads as the same page rather than a blank card. Returns false if the
// page's canvas isn't rendered yet (try again next frame).
const paintShowThrough = (paperEl, pageEl) => {
    const src = pageEl && pageEl.querySelector('canvas');
    if (!paperEl || !src || !src.width) return false;
    let canvas = paperEl.querySelector('canvas');
    if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'fb-show-through';
        paperEl.appendChild(canvas);
    }
    canvas.width = src.width;
    canvas.height = src.height;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(-1, 0, 0, 1, canvas.width, 0);
    ctx.globalAlpha = 0.16;
    ctx.drawImage(src, 0, 0);
    return true;
};

// --- Page --------------------------------------------------------------------

// hiDpr: while zoomed, on-screen pages get a second, sharper canvas on top of
// the normal one. Re-rendering the base canvas instead would blank it for a
// moment; this way the soft version shows until the sharp one is painted.
const BookPage = React.memo(({ num, W, H, side, hiDpr, hotspots, registerRef, onGoToPage }) => (
    <div className="fb-page" data-side={side} ref={(el) => registerRef(num, el)} style={{ width: W, height: H }}>
        <Page
            pageNumber={num}
            width={W}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            devicePixelRatio={CANVAS_DPR}
            loading={null}
        />
        {hiDpr && (
            <Page
                className="fb-hires"
                pageNumber={num}
                width={W}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                devicePixelRatio={hiDpr}
                loading={null}
            />
        )}
        <div className="fb-gutter" />
        <PageOverlays hotspots={hotspots} onGoToPage={onGoToPage} />
    </div>
));

// --- Book --------------------------------------------------------------------

const LAYOUT_MODES = ['auto', 'single', 'double'];

const Book = ({ documentUrl, dlEnabled, showBranding, brandingLink, brandName = 'PDF Deck', brandLogo, brandColor, title, cta, reportUrl, tracking, layout: layoutSetting, hotspots: authoredHotspots }) => {
    const [numPages, setNumPages] = useState(null);
    const [aspect, setAspect] = useState(null);
    const [page, setPage] = useState(1);
    const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
    const [pdfLinks, setPdfLinks] = useState({});
    const [chromeVisible, setChromeVisible] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [pinched, setPinched] = useState(false);
    // The document's layout setting, until the viewer flips it with the toggle.
    const [layoutMode, setLayoutMode] = useState(LAYOUT_MODES.includes(layoutSetting) ? layoutSetting : 'auto');
    // Zoom level as React sees it (drives the sharp layer + the zoom buttons).
    // The live transform is in zoomRef and written straight to the DOM.
    const [zoom, setZoom] = useState(1);

    const isDlEnabled = dlEnabled === 'true';
    const doShowBranding = showBranding === 'true';

    const layout = aspect ? computeLayout(viewport.w, viewport.h, aspect, layoutMode) : null;
    const spread = layout ? layout.spread : false;
    const W = layout ? layout.W : 0;
    const H = layout ? layout.H : 0;

    // Mutable engine state read by applyLayout() and the animation loop.
    const eng = useRef({ spread, W, H, n: 0, page: 1, flip: null, P: null });
    const pageEls = useRef(new Map());
    const stageRef = useRef(null);
    const baseRef = useRef(null);
    const paperRef = useRef(null);
    const underShadowRef = useRef(null);
    const frontShadeRef = useRef(null);
    const dropRef = useRef(null);
    const backShadeRef = useRef(null);
    const animRef = useRef(null);
    const rafRef = useRef(null);
    const pressRef = useRef(null);
    const pdfRef = useRef(null);
    const rootRef = useRef(null);
    const hideTimerRef = useRef(null);
    const zoomWrapRef = useRef(null);
    const zoomRef = useRef({ z: 1, x: 0, y: 0 });
    const zoomCommitRef = useRef(null);

    const { left: visLeft, right: visRight } = numPages ? visiblePages(spread, page, numPages) : { left: null, right: null };
    const recordClick = usePageTracking({ tracking, currentPage: [visLeft, visRight].filter(Boolean), numPages });

    const registerRef = useCallback((num, el) => {
        if (el) pageEls.current.set(num, el);
        else pageEls.current.delete(num);
    }, []);

    // --- applyLayout: the only place page/shadow geometry is written -------

    const applyLayout = useCallback(() => {
        const e = eng.current;
        if (!e.W || !e.n || !paperRef.current) return;
        const { W, H } = e;
        const spineX = e.spread ? W : 0;
        const els = pageEls.current;

        const reset = (el) => {
            el.style.visibility = '';
            el.style.transform = '';
            el.style.clipPath = '';
            el.style.webkitClipPath = '';
            el.style.zIndex = '';
            el.style.opacity = '';
        };
        els.forEach(reset);
        reset(paperRef.current);

        const sideX = (side) => (side === 'left' ? spineX - W : spineX);
        const place = (num, side, z) => {
            const el = num === 'paper' ? paperRef.current : els.get(num);
            if (!el) return null;
            el.style.visibility = 'visible';
            el.style.transform = `translate3d(${sideX(side)}px,0,0)`;
            el.style.zIndex = z;
            return el;
        };
        const setClip = (el, poly) => {
            const css = poly ? polygonCss(poly) : '';
            el.style.clipPath = css;
            el.style.webkitClipPath = css;
        };
        const hide = (el) => { el.style.display = 'none'; };
        const show = (el) => { el.style.display = 'block'; };
        const shadowEls = [underShadowRef, frontShadeRef, dropRef, backShadeRef].map((r) => r.current);

        // Base: the soft shadow under whichever page slots are occupied.
        const occupied = { left: false, right: false };
        const markSide = (num, side) => { if (num) occupied[side] = true; };

        const f = e.flip;
        let geo = null;
        if (f) {
            const P = constrainCorner(e.P, f.corner, W, H);
            geo = solveFold({ W, H, dir: f.dir, corner: f.corner, P, spineX });
        }

        if (!f || !geo) {
            shadowEls.forEach(hide);
            if (f) {
                // Sheet is flat: the front page lies on top of everything.
                const frontSide = f.dir > 0 ? 'right' : 'left';
                const otherSide = frontSide === 'right' ? 'left' : 'right';
                place(f.front, frontSide, Z.front);
                markSide(f.front, frontSide);
                if (f.other) { place(f.other, otherSide, Z.page); markSide(f.other, otherSide); }
            } else {
                const vis = visiblePages(e.spread, e.page, e.n);
                if (vis.left) { place(vis.left, 'left', Z.page); markSide(vis.left, 'left'); }
                if (vis.right) { place(vis.right, 'right', Z.page); markSide(vis.right, 'right'); }
            }
        } else {
            const frontSide = f.dir > 0 ? 'right' : 'left';
            const otherSide = frontSide === 'right' ? 'left' : 'right';
            if (f.other) { place(f.other, otherSide, Z.page); markSide(f.other, otherSide); }
            if (f.under) { place(f.under, frontSide, Z.page); markSide(f.under, frontSide); }
            const front = place(f.front, frontSide, Z.front);
            if (front) setClip(front, geo.frontClip);
            markSide(f.front, frontSide);
            const backEl = f.back === 'paper' ? paperRef.current : els.get(f.back);
            // One-page mode only: the flap fades away as the turn completes.
            // Late in the turn, so it happens mostly during the automatic finish rather than under the finger.
            const flapOpacity = f.back === 'paper' ? 1 - smoothstep(0.65, 1.0, geo.progress) : 1;
            if (f.back === 'paper' && e.paperFor !== f.front) {
                if (paintShowThrough(paperRef.current, els.get(f.front))) e.paperFor = f.front;
            }
            if (backEl) {
                backEl.style.visibility = 'visible';
                backEl.style.transform = matrixCss(geo.backMatrix);
                backEl.style.zIndex = Z.back;
                setClip(backEl, geo.backClip);
                if (flapOpacity < 1) backEl.style.opacity = flapOpacity.toFixed(3);
            }

            // Shadow strips: each is a long gradient bar whose x axis runs along
            // the fold's normal, laid inside a wrapper clipped to its region.
            const s = geo.progress;
            const lift = Math.sin(Math.PI * Math.min(1, s));
            const angle = Math.atan2(geo.normalPx.y, geo.normalPx.x);
            const L = 3 * Math.hypot(W, H);
            const strip = (wrapper, poly, towardCorner, width, gradient) => {
                show(wrapper);
                setClip(wrapper, poly);
                const bar = wrapper.firstChild;
                const a = towardCorner ? angle : angle + Math.PI;
                bar.style.width = `${Math.max(1, width)}px`;
                bar.style.height = `${L}px`;
                bar.style.transform = `translate(${geo.foldMidPx.x}px, ${geo.foldMidPx.y}px) rotate(${a}rad) translate(0, ${-L / 2}px)`;
                bar.style.background = gradient;
            };
            // Cast by the lifted sheet onto the page coming into view.
            strip(shadowEls[0], geo.uncoveredPx, true, W * (0.06 + 0.3 * lift),
                `linear-gradient(to right, rgba(0,0,0,${(0.18 + 0.32 * lift).toFixed(3)}), rgba(0,0,0,0))`);
            // The flat part of the sheet darkens toward the bend.
            strip(shadowEls[1], geo.flatPx, false, W * 0.18,
                `linear-gradient(to left, rgba(0,0,0,${(0.12 * lift).toFixed(3)}), rgba(0,0,0,0))`);
            // Curl highlight on the back face: crease shadow, a sheen, then falloff.
            strip(shadowEls[3], geo.liftedPx, false, W * (0.25 + 0.2 * lift),
                `linear-gradient(to right, rgba(0,0,0,${(0.10 + 0.12 * lift).toFixed(3)}) 0%, rgba(255,255,255,${(0.16 * lift).toFixed(3)}) 18%, rgba(0,0,0,${(0.06 * lift).toFixed(3)}) 55%, rgba(0,0,0,0) 100%)`);
            if (flapOpacity < 1) {
                shadowEls[3].style.opacity = flapOpacity.toFixed(3);
                shadowEls[0].style.opacity = flapOpacity.toFixed(3);
            } else {
                shadowEls[3].style.opacity = '';
                shadowEls[0].style.opacity = '';
            }
            // Soft drop shadow of the flap onto whatever is beneath it.
            show(shadowEls[2]);
            const dropShape = shadowEls[2].firstChild;
            // The shape box extends one stage-size past every edge (see .fb-drop),
            // so the flap's shadow still draws when it hangs off the stage.
            const sw = e.spread ? 2 * W : W;
            setClip(dropShape, geo.liftedPx.map((p) => ({ x: p.x + sw, y: p.y + H })));
            dropShape.style.opacity = ((0.35 + 0.35 * lift) * flapOpacity).toFixed(3);
        }

        const base = baseRef.current;
        if (base) {
            const x0 = occupied.left ? spineX - W : spineX;
            const x1 = occupied.right ? spineX + W : spineX;
            base.style.left = `${x0}px`;
            base.style.width = `${Math.max(0, x1 - x0)}px`;
        }

        // Centre the closed book (cover / back cover) on the spine.
        const stage = stageRef.current;
        if (stage && e.spread) {
            let vis = visiblePages(e.spread, e.page, e.n);
            if (f && geo && geo.progress > 0.5 && !f.reverse) vis = visiblePages(e.spread, f.target, e.n);
            const shift = !vis.left ? -W / 2 : !vis.right ? W / 2 : 0;
            stage.style.transform = `translate3d(${shift}px, 0, 0)`;
        } else if (stage) {
            stage.style.transform = '';
        }
    }, []);

    // --- Animation loop ----------------------------------------------------

    const tick = useCallback((now) => {
        const a = animRef.current;
        const e = eng.current;
        if (!a || !e.flip) {
            rafRef.current = null;
            return;
        }
        if (a.type === 'tween') {
            if (a.start == null) a.start = now;
            const t = Math.min(1, (now - a.start) / a.dur);
            e.P = bezier(a.from, a.ctrl, a.to, easeInOutCubic(t));
            applyLayout();
            if (t >= 1) {
                animRef.current = null;
                rafRef.current = null;
                a.onDone();
                return;
            }
        } else {
            // follow: ease the corner toward the pointer
            e.P = { x: e.P.x + (a.target.x - e.P.x) * a.k, y: e.P.y + (a.target.y - e.P.y) * a.k };
            applyLayout();
        }
        rafRef.current = requestAnimationFrame(tick);
    }, [applyLayout]);

    const runAnim = useCallback((anim) => {
        animRef.current = anim;
        if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    }, [tick]);

    // Jump a running tween straight to its end (so rapid clicks never queue up).
    const fastForward = useCallback(() => {
        const a = animRef.current;
        if (a && a.type === 'tween') {
            if (FB_DEBUG) console.log('[flipbook] animation skipped to its end'); // eslint-disable-line no-console
            animRef.current = null;
            eng.current.P = a.to;
            a.onDone();
        }
    }, []);

    const restPoint = (f) => cornerPoint(f.corner, eng.current.W, eng.current.H);
    const overPoint = (f) => ({ x: -eng.current.W, y: restPoint(f).y });

    const commit = useCallback((target) => {
        const e = eng.current;
        e.flip = null;
        e.page = target;
        applyLayout();
        setPage(target);
    }, [applyLayout]);

    const settle = useCallback((complete) => {
        const e = eng.current;
        const f = e.flip;
        if (!f) return;
        const { W, H } = e;
        const from = constrainCorner(e.P, f.corner, W, H);
        const to = complete !== f.reverse ? overPoint(f) : restPoint(f);
        const span = Math.abs(to.x - from.x) / (2 * W);
        // Lift the corner on the way over so the fold sweeps diagonally.
        const liftDir = f.corner === 'bottom' ? -1 : 1;
        const ctrl = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 + liftDir * H * 0.28 * span };
        runAnim({
            type: 'tween',
            from,
            ctrl,
            to,
            dur: 220 + 520 * span,
            onDone: () => {
                if (FB_DEBUG) console.log('[flipbook] turn finished:', complete ? 'page ' + f.target : 'fell back'); // eslint-disable-line no-console
                if (complete) commit(f.target);
                else {
                    e.flip = null;
                    applyLayout();
                }
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runAnim, commit, applyLayout]);

    const beginFlip = (plan, extra) => {
        const e = eng.current;
        e.flip = { ...plan, ...extra };
        e.P = plan.reverse ? overPoint(plan) : restPoint(plan);
    };

    const turn = useCallback((which, corner = 'bottom') => {
        const e = eng.current;
        if (!e.n || !e.W) return;
        fastForward();
        if (e.flip && e.flip.peel) {
            const peelWhich = e.flip.which;
            if (peelWhich === which) {
                e.flip.peel = false;
                settle(true);
                return;
            }
            animRef.current = null;
            e.flip = null;
        }
        if (e.flip) return;
        const plan = planFlip(e.spread, e.page, e.n, which, corner);
        if (!plan) return;
        beginFlip(plan, { which });
        settle(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fastForward, settle]);

    const goToPage = useCallback((target) => {
        const e = eng.current;
        if (!e.n) return;
        if (FB_DEBUG) console.log('[flipbook] goToPage', target, e.flip ? '(cancelling a turn)' : ''); // eslint-disable-line no-console
        animRef.current = null;
        e.flip = null;
        commit(Math.max(1, Math.min(e.n, target)));
    }, [commit]);

    // --- Pointer interaction ----------------------------------------------

    const toStage = (ev) => {
        const r = stageRef.current.getBoundingClientRect();
        return { x: ev.clientX - r.left, y: ev.clientY - r.top };
    };
    const toCanon = (pt, f) => {
        const e = eng.current;
        const spineX = e.spread ? e.W : 0;
        return { x: f.dir * (pt.x - spineX), y: pt.y };
    };

    // Outer page corners that can be peeled on hover (desktop only).
    const cornerAt = (pt) => {
        const e = eng.current;
        const { W, H } = e;
        const r = Math.max(40, Math.min(110, W * 0.14));
        const spineX = e.spread ? W : 0;
        const candidates = [
            { which: 'next', x: spineX + W },
            ...(e.spread ? [{ which: 'prev', x: spineX - W }] : []),
        ];
        for (const c of candidates) {
            for (const corner of ['top', 'bottom']) {
                const cy = corner === 'top' ? 0 : H;
                if (Math.hypot(pt.x - c.x, pt.y - cy) < r) return { which: c.which, corner, r };
            }
        }
        return null;
    };

    const onPointerDown = (ev) => {
        if (pinched || !eng.current.n || ev.button > 0) return;
        if (zoomRef.current.z > 1.01) {
            // Zoomed in: dragging pans the book instead of turning pages.
            const { x, y } = zoomRef.current;
            pressRef.current = { id: ev.pointerId, pan: { cx: ev.clientX, cy: ev.clientY, x, y } };
            try { stageRef.current.setPointerCapture(ev.pointerId); } catch (err) { /* ignore */ }
            return;
        }
        if (pressRef.current) {
            if (FB_DEBUG) console.log('[flipbook] new press while a drag was open: settling it'); // eslint-disable-line no-console
            releaseRef.current(true);  // a drag left open (e.g. released outside a frame)
        }
        fastForward();
        const pt = toStage(ev);
        const e = eng.current;
        pressRef.current = {
            id: ev.pointerId,
            start: pt,
            samples: [{ x: pt.x, t: ev.timeStamp }],
            t0: ev.timeStamp,
            dragging: false,
            peel: e.flip && e.flip.peel ? e.flip : null,
        };
    };

    const onPointerMove = (ev) => {
        const e = eng.current;
        if (!e.n || !stageRef.current) return;
        const pt = toStage(ev);
        const press = pressRef.current;

        if (press && press.pan) {
            if (press.id !== ev.pointerId) return;
            const zr = zoomRef.current;
            zr.x = press.pan.x + ev.clientX - press.pan.cx;
            zr.y = press.pan.y + ev.clientY - press.pan.cy;
            applyZoom();
            return;
        }
        if (!press) {
            // Hover peel for mouse users.
            if (ev.pointerType !== 'mouse' || pinched || zoomRef.current.z > 1.01) return;
            if (animRef.current && animRef.current.type === 'tween') return;
            const hit = cornerAt(pt);
            if (e.flip && e.flip.peel) {
                if (hit && hit.which === e.flip.which && hit.corner === e.flip.corner) {
                    runAnim({ type: 'follow', target: toCanon(pt, e.flip), k: 0.25 });
                } else {
                    if (FB_DEBUG) console.log('[flipbook] hover peel cancelled (pointer moved away)'); // eslint-disable-line no-console
                    e.flip.peel = false;
                    settle(false);
                }
            } else if (hit && !e.flip) {
                const plan = planFlip(e.spread, e.page, e.n, hit.which, hit.corner);
                if (plan && !plan.reverse) {
                    if (FB_DEBUG) console.log('[flipbook] hover peel started', hit.which, 'buttons:', ev.buttons); // eslint-disable-line no-console
                    beginFlip(plan, { which: hit.which, peel: true });
                    runAnim({ type: 'follow', target: toCanon(pt, e.flip), k: 0.25 });
                }
            }
            return;
        }
        if (press.id !== ev.pointerId) return;
        // The button was released somewhere we never heard about (outside the
        // window, over browser chrome) — finish the drag now instead of sticking.
        if (ev.pointerType === 'mouse' && ev.buttons === 0) {
            if (FB_DEBUG) console.log('[flipbook] move with no button held: ending drag'); // eslint-disable-line no-console
            onPointerUp({ pointerId: ev.pointerId, type: 'pointercancel' });
            return;
        }

        press.samples.push({ x: pt.x, t: ev.timeStamp });
        while (press.samples.length > 2 && ev.timeStamp - press.samples[1].t > VELOCITY_WINDOW_MS) press.samples.shift();

        const dx = pt.x - press.start.x;
        const dy = pt.y - press.start.y;

        if (!press.dragging) {
            if (Math.abs(dx) < DRAG_START_PX || Math.abs(dx) < Math.abs(dy)) return;
            if (press.peel && e.flip === press.peel) {
                press.mode = 'direct';
            } else {
                if (e.flip) return;
                let which;
                if (e.spread) {
                    which = press.start.x < e.W ? 'prev' : 'next';
                    if ((which === 'next' && dx > 0) || (which === 'prev' && dx < 0)) {
                        pressRef.current = null;
                        return;
                    }
                } else {
                    which = dx < 0 ? 'next' : 'prev';
                }
                const corner = press.start.y < e.H / 2 ? 'top' : 'bottom';
                const plan = planFlip(e.spread, e.page, e.n, which, corner);
                if (!plan) {
                    pressRef.current = null;
                    return;
                }
                beginFlip(plan, { which });
                press.mode = 'delta';
                press.base = { ...e.P };
                // Scale the drag so the turn completes by the time the pointer
                // reaches the book's far edge (the page's left edge in one-page
                // mode) — a grab mid-page never runs out of screen first.
                const startX = toCanon(press.start, e.flip).x;
                const travel = plan.reverse ? e.W - startX : startX + (e.spread ? e.W : 0);
                // One-page mode: close to 1:1 with the finger (a 2x gain made the
                // sheet race ahead and feel sudden). Crossing the page gets past
                // halfway, and a release past halfway finishes the turn.
                press.gain = e.spread
                    ? Math.min(3, Math.max(1, (2 * e.W) / Math.max(1, travel)))
                    : Math.min(1.4, Math.max(1, (1.3 * e.W) / Math.max(1, travel)));
            }
            press.dragging = true;
            e.flip.peel = false;
            try { stageRef.current.setPointerCapture(ev.pointerId); } catch (err) { /* ignore */ }
        }

        const f = e.flip;
        if (!f) return;
        const target = press.mode === 'direct'
            ? toCanon(pt, f)
            : { x: press.base.x + f.dir * dx * press.gain, y: press.base.y + dy };
        press.target = target;
        runAnim({ type: 'follow', target, k: 0.5 });
        // In a frame (settings preview, embeds) the browser sends nothing once
        // the cursor is outside it, not even the pointerup, so a drag reaching
        // the frame's edge is treated as leaving: it ends as a throw.
        if (IN_FRAME) {
            // Once the cursor is outside a frame, nothing more arrives (not even
            // the pointerup), and a fast move can jump straight out. So if the
            // drag goes quiet near the edge, assume it left and end it as a
            // throw. (Never end it while moves are still coming: the hand is
            // still on it.)
            const edgeDist = Math.min(ev.clientX, ev.clientY, window.innerWidth - ev.clientX, window.innerHeight - ev.clientY);
            clearTimeout(press.idleTimer);
            if (edgeDist < Math.max(120, 0.12 * Math.min(window.innerWidth, window.innerHeight))) {
                press.idleTimer = setTimeout(() => {
                    if (pressRef.current !== press) return;
                    if (FB_DEBUG) console.log('[flipbook] drag ended: went quiet near the frame edge'); // eslint-disable-line no-console
                    releaseRef.current(true);
                }, FRAME_IDLE_MS);
            }
        }
    };

    const onPointerUp = (ev) => {
        const press = pressRef.current;
        if (!press || press.id !== ev.pointerId) return;
        pressRef.current = null;
        clearTimeout(press.idleTimer);
        if (press.pan) return;
        const e = eng.current;

        if (press.dragging && e.flip) {
            const f = e.flip;
            // Judge where the finger is, not the eased corner trailing behind it.
            const P = constrainCorner(press.target || e.P, f.corner, e.W, e.H);
            const progress = (e.W - P.x) / (2 * e.W); // 0 at rest, 1 fully over
            const first = press.samples[0];
            const last = press.samples[press.samples.length - 1];
            // A finger that stopped before lifting isn't flicking.
            const now = ev.timeStamp || performance.now();
            const vx = now - last.t > VELOCITY_WINDOW_MS ? 0 : (last.x - first.x) / Math.max(16, last.t - first.t);
            // The finger's own speed (not scaled by gain: a small backwards
            // twitch while lifting shouldn't read as a throw).
            const v = f.dir * vx; // canonical: negative = toward "over"
            const mode = e.spread ? 'spread' : 'single';
            const at = COMPLETE_AT[mode];
            const flick = FLICK[mode];
            // How far through the turn, measured in the direction it's going.
            const done = f.reverse ? 1 - progress : progress;
            const toward = f.reverse ? -v : v;       // negative = moving toward finishing
            // Net movement over the whole press, in the turning direction (negative = toward finishing).
            const net = f.dir * (last.x - press.start.x) * (f.reverse ? -1 : 1);
            const swipe = now - (press.t0 || now) < SWIPE_MS && net < -SWIPE_PX;
            let complete;
            if (swipe && !(toward > flick)) complete = true;      // a quick swipe turns the page
            else if (done > 0.5) complete = !(toward > 2 * flick);   // past halfway: finish unless clearly thrown back
            else if (ev.leaving) complete = toward <= 0 || done > at; // dragged off the edge: carry on the way it was going
            else complete = toward < -flick || (toward <= flick && done > at);
            if (FB_DEBUG) {
                // eslint-disable-next-line no-console
                console.log('[flipbook] release', {
                    complete, which: f.which, reverse: f.reverse, mode: press.mode, layout: e.spread ? 'two-page' : 'one-page',
                    done: +done.toFixed(3), speed: +toward.toFixed(3), net: Math.round(net), swipe, leaving: !!ev.leaving,
                    pressMs: Math.round(now - (press.t0 || now)), sinceLastMoveMs: Math.round(now - last.t), samples: press.samples.length, event: ev.type,
                });
            }
            settle(complete);
            return;
        }
        if (FB_DEBUG) console.log('[flipbook] release without drag', { event: ev.type, dragging: press.dragging }); // eslint-disable-line no-console
        if (ev.type === 'pointercancel') return;

        // A click: turn toward the side that was clicked.
        if (press.peel && e.flip === press.peel) {
            e.flip.peel = false;
            settle(true);
            return;
        }
        const pt = press.start;
        const corner = pt.y < e.H / 2 ? 'top' : 'bottom';
        const which = e.spread ? (pt.x < e.W ? 'prev' : 'next') : (pt.x < e.W * 0.35 ? 'prev' : 'next');
        turn(which, corner);
    };

    // Every other way a drag can end without a pointerup reaching the stage:
    // capture lost, the cursor leaving the window, the window losing focus.
    // The page settles from wherever it is, as if released there.
    const releaseRef = useRef(null);
    releaseRef.current = (leaving) => {
        const press = pressRef.current;
        if (press) onPointerUp({ pointerId: press.id, type: 'pointercancel', leaving: !!leaving });
    };
    useEffect(() => {
        const release = () => releaseRef.current();
        // Leaving the document (the window, or the frame the viewer is in, e.g.
        // the settings preview or an embed): a frame gets no pointerup once the
        // cursor is outside it, so the drag ends here, as a throw in the
        // direction it was going (onPointerUp, `leaving`).
        const onOut = (ev) => {
            if (ev.relatedTarget) return;
            if (FB_DEBUG && pressRef.current) console.log('[flipbook] drag ended: pointer left the document', { target: ev.target && ev.target.className, x: Math.round(ev.clientX), y: Math.round(ev.clientY), buttons: ev.buttons }); // eslint-disable-line no-console
            releaseRef.current(true);
        };
        window.addEventListener('pointerup', release);
        window.addEventListener('blur', release);
        document.addEventListener('pointerout', onOut);
        return () => {
            window.removeEventListener('pointerup', release);
            window.removeEventListener('blur', release);
            document.removeEventListener('pointerout', onOut);
        };
    }, []);

    const onPointerLeave = (ev) => {
        const e = eng.current;
        if (!pressRef.current && e.flip && e.flip.peel && ev.pointerType === 'mouse') {
            e.flip.peel = false;
            settle(false);
        }
    };

    // --- Zoom --------------------------------------------------------------
    //
    // The book is scaled/panned by a transform on .fb-zoom (around its centre),
    // written directly like applyLayout so wheel/pinch stays at frame rate.
    // React's `zoom` state follows a beat later to swap in sharper canvases.

    const applyZoom = useCallback(() => {
        const el = zoomWrapRef.current;
        const zr = zoomRef.current;
        if (!el) return;
        const e = eng.current;
        const sw = (e.spread ? 2 * e.W : e.W) * zr.z;
        const sh = e.H * zr.z;
        // Keep some of the book on screen whichever way it's dragged.
        const maxX = Math.max(0, (sw - window.innerWidth) / 2 + 40);
        const maxY = Math.max(0, (sh - window.innerHeight) / 2 + 40);
        zr.x = Math.max(-maxX, Math.min(maxX, zr.x));
        zr.y = Math.max(-maxY, Math.min(maxY, zr.y));
        el.style.transform = zr.z === 1 ? '' : `translate3d(${zr.x}px, ${zr.y}px, 0) scale(${zr.z})`;
    }, []);

    // Zoom to `z`, keeping the screen point `at` (default: screen centre) fixed.
    const zoomTo = useCallback((z, at) => {
        const zr = zoomRef.current;
        const next = Math.max(1, Math.min(MAX_ZOOM, z));
        const el = zoomWrapRef.current;
        if (!el) return;
        if (next <= 1.001) {
            zr.z = 1; zr.x = 0; zr.y = 0;
        } else {
            // Untransformed centre of the book on screen.
            const r = el.getBoundingClientRect();
            const cx = r.left + r.width / 2 - zr.x;
            const cy = r.top + r.height / 2 - zr.y;
            const px = at ? at.x : window.innerWidth / 2;
            const py = at ? at.y : window.innerHeight / 2;
            // The book point under (px, py) stays under it.
            const ux = (px - cx - zr.x) / zr.z;
            const uy = (py - cy - zr.y) / zr.z;
            zr.x = px - cx - next * ux;
            zr.y = py - cy - next * uy;
            zr.z = next;
        }
        if (eng.current.flip && eng.current.flip.peel) {
            if (FB_DEBUG) console.log('[flipbook] zoom cancelled a hover peel'); // eslint-disable-line no-console
            animRef.current = null;
            eng.current.flip = null;
            applyLayout();
        }
        applyZoom();
        clearTimeout(zoomCommitRef.current);
        zoomCommitRef.current = setTimeout(() => setZoom(zr.z), 200);
    }, [applyZoom, applyLayout]);

    const zoomStep = (dir) => {
        const z = zoomRef.current.z;
        zoomTo(dir > 0 ? z * 1.5 : z / 1.5 < 1.1 ? 1 : z / 1.5);
    };

    // ctrl/⌘ + wheel (and trackpad pinch, which arrives as ctrl+wheel) zooms
    // around the cursor; plain wheel pans while zoomed. Native listener so we
    // can preventDefault the browser's own page zoom.
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        const onWheel = (ev) => {
            if (ev.ctrlKey || ev.metaKey) {
                ev.preventDefault();
                zoomTo(zoomRef.current.z * Math.exp(-ev.deltaY * 0.01), { x: ev.clientX, y: ev.clientY });
            } else if (zoomRef.current.z > 1.01) {
                ev.preventDefault();
                zoomRef.current.x -= ev.deltaX;
                zoomRef.current.y -= ev.deltaY;
                applyZoom();
            }
        };
        root.addEventListener('wheel', onWheel, { passive: false });
        return () => root.removeEventListener('wheel', onWheel);
    }, [zoomTo, applyZoom]);

    // A layout change (viewer toggle, rotate) re-fits the book: start unzoomed.
    useEffect(() => { zoomTo(1); }, [spread, zoomTo]);
    useEffect(() => () => clearTimeout(zoomCommitRef.current), []);

    // --- Effects -----------------------------------------------------------

    // Sync render-derived values into the engine, then lay out before paint.
    useLayoutEffect(() => {
        const e = eng.current;
        const changed = e.spread !== spread || e.W !== W || e.H !== H;
        e.spread = spread;
        e.W = W;
        e.H = H;
        e.n = numPages || 0;
        e.page = page;
        if (changed && e.flip) {
            if (FB_DEBUG) console.log('[flipbook] turn CANCELLED: layout changed mid-turn', { spread, W, H }); // eslint-disable-line no-console
            animRef.current = null;
            e.flip = null;
        }
        applyLayout();
        applyZoom();
    });

    useEffect(() => {
        let t;
        const onResize = () => {
            clearTimeout(t);
            t = setTimeout(() => setViewport({ w: window.innerWidth, h: window.innerHeight }), 60);
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            clearTimeout(t);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, []);

    // When the visitor pinch-zooms on mobile, hand panning back to the browser.
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return undefined;
        const onVV = () => setPinched(vv.scale > 1.05);
        vv.addEventListener('resize', onVV);
        return () => vv.removeEventListener('resize', onVV);
    }, []);

    useEffect(() => {
        const onKey = (ev) => {
            if (ev.target && (ev.target.tagName === 'INPUT' || ev.target.tagName === 'TEXTAREA')) return;
            const k = ev.key;
            if (k === 'ArrowRight' || k === 'PageDown' || k === ' ') { ev.preventDefault(); turn('next'); }
            else if (k === 'ArrowLeft' || k === 'PageUp') { ev.preventDefault(); turn('prev'); }
            else if (k === 'Home') { ev.preventDefault(); goToPage(1); }
            else if (k === 'End') { ev.preventDefault(); goToPage(eng.current.n); }
            else if (k === 'f' || k === 'F') { ev.preventDefault(); toggleFullscreen(); }
            else if ((k === '+' || k === '=') && !ev.metaKey && !ev.ctrlKey) { ev.preventDefault(); zoomStep(1); }
            else if (k === '-' && !ev.metaKey && !ev.ctrlKey) { ev.preventDefault(); zoomStep(-1); }
            else if (k === '0' && !ev.metaKey && !ev.ctrlKey) { ev.preventDefault(); zoomTo(1); }
            else return;
            revealChrome();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [turn, goToPage, zoomTo]);

    useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

    // Pull link annotations for pages as they come into the mounted window.
    const mounted = useMemo(() => {
        if (!numPages) return [];
        const lo = spread ? Math.floor(page / 2) * 2 - 3 : page - 2;
        const hi = spread ? Math.floor(page / 2) * 2 + 4 : page + 2;
        const out = [];
        for (let i = Math.max(1, lo); i <= Math.min(numPages, hi); i++) out.push(i);
        return out;
    }, [numPages, spread, page]);

    useEffect(() => {
        const pdf = pdfRef.current;
        if (!pdf) return;
        mounted.forEach((num) => {
            if (pdfLinks[num] !== undefined) return;
            setPdfLinks((prev) => ({ ...prev, [num]: null })); // in flight
            extractPdfLinks(pdf, num)
                .then((links) => setPdfLinks((prev) => ({ ...prev, [num]: links })))
                .catch(() => setPdfLinks((prev) => ({ ...prev, [num]: [] })));
        });
    }, [mounted, pdfLinks]);

    const hotspotsByPage = useMemo(() => {
        const out = {};
        Object.keys(pdfLinks).forEach((num) => { if (pdfLinks[num]) out[num] = [...pdfLinks[num]]; });
        (authoredHotspots || []).forEach((h) => { (out[h.page] = out[h.page] || []).push(h); });
        return out;
    }, [pdfLinks, authoredHotspots]);

    // --- Chrome ------------------------------------------------------------

    const revealChrome = useCallback(() => {
        setChromeVisible(true);
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setChromeVisible(false), 3000);
    }, []);

    useEffect(() => {
        const on = () => revealChrome();
        document.addEventListener('mousemove', on);
        document.addEventListener('touchstart', on, { passive: true });
        revealChrome();
        return () => {
            document.removeEventListener('mousemove', on);
            document.removeEventListener('touchstart', on);
            clearTimeout(hideTimerRef.current);
        };
    }, [revealChrome]);

    const toggleFullscreen = () => {
        const el = rootRef.current;
        if (!el) return;
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        } else {
            (el.requestFullscreen || el.webkitRequestFullscreen).call(el);
        }
    };

    useEffect(() => {
        const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
        document.addEventListener('fullscreenchange', onChange);
        document.addEventListener('webkitfullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            document.removeEventListener('webkitfullscreenchange', onChange);
        };
    }, []);

    // A click on the background (beside the book) turns that way, but only if
    // the press started there too: a page drag released beside the book also
    // fires a click here, which used to turn the page straight back.
    const backgroundPressRef = useRef(false);
    const BACKGROUND_EXCLUDE = '.fb-stage, [data-fb-chrome], a, button';
    const onBackgroundPointerDown = (ev) => {
        backgroundPressRef.current = !ev.target.closest(BACKGROUND_EXCLUDE);
    };
    const onBackgroundClick = (ev) => {
        const startedHere = backgroundPressRef.current;
        backgroundPressRef.current = false;
        if (!startedHere || ev.target.closest(BACKGROUND_EXCLUDE)) return;
        if (FB_DEBUG) console.log('[flipbook] background click: turning', ev.clientX > window.innerWidth / 2 ? 'next' : 'prev'); // eslint-disable-line no-console
        turn(ev.clientX > window.innerWidth / 2 ? 'next' : 'prev');
    };

    const onDocumentLoadSuccess = async (pdf) => {
        pdfRef.current = pdf;
        const first = await pdf.getPage(1);
        const vp = first.getViewport({ scale: 1 });
        setAspect(vp.width / vp.height);
        setNumPages(pdf.numPages);
    };

    const atStart = page <= 1;
    const atEnd = numPages ? (spread ? Math.floor(page / 2) * 2 + 2 > numPages : page >= numPages) : true;
    const label = numPages
        ? (visLeft && visRight ? `${visLeft}–${visRight}` : `${visLeft || visRight}`)
        : '';
    const shownMax = Math.max(visLeft || 0, visRight || 0);
    const progressPct = numPages ? (shownMax / numPages) * 100 : 0;
    const stageW = spread ? W * 2 : W;
    const isZoomed = zoom > 1.01;
    const baseDpr = CANVAS_DPR || window.devicePixelRatio || 1;
    const hiDprRaw = W ? Math.min(baseDpr * zoom, Math.sqrt(HIRES_MAX_PIXELS / (W * H))) : 0;
    const hiDpr = isZoomed && hiDprRaw > baseDpr * 1.05 ? hiDprRaw : null;
    const spreadPossible = canShowSpread(viewport.w, viewport.h);

    return (
        <div
            ref={rootRef}
            className={`fb-root${chromeVisible ? '' : ' fb-chrome-hidden'}`}
            data-page={page}
            onPointerDownCapture={onBackgroundPointerDown}
            onClick={onBackgroundClick}
        >
            {/* Top bar: the sharer's brand on the left, actions on the right.
                Always visible (it's their branding), and opaque so it stays
                readable when the zoomed book runs underneath it. */}
            <header className="fb-bar" data-fb-chrome>
                <div className="fb-bar-brand">
                    {brandLogo && <img className="fb-bar-logo" src={brandLogo} alt="" />}
                    {title && <span className="fb-bar-title">{title}</span>}
                </div>
                <div className="fb-bar-actions">
                    {reportUrl && (
                        <a className="fb-icon" href={reportUrl} target="_blank" rel="noopener noreferrer" title="Report this content">
                            <Icon path="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
                        </a>
                    )}
                    <IconButton
                        onClick={() => zoomStep(-1)}
                        disabled={!isZoomed}
                        title="Zoom out (−)"
                        path="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM13.5 10.5h-6"
                    />
                    <IconButton
                        onClick={() => zoomStep(1)}
                        disabled={zoom >= MAX_ZOOM}
                        title="Zoom in (+)"
                        path="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6"
                    />
                    {spreadPossible && (
                        <IconButton
                            onClick={() => setLayoutMode(spread ? 'single' : 'double')}
                            title={spread ? 'Single page' : 'Two-page spread'}
                            path={spread
                                ? 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z'
                                : 'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25'}
                        />
                    )}
                    <IconButton
                        onClick={toggleFullscreen}
                        title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}
                        path={isFullscreen
                            ? 'M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25'
                            : 'M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15'}
                    />
                    {isDlEnabled && (
                        <a className="fb-icon" href={documentUrl} download="flipbook.pdf" title="Download PDF">
                            <Icon path="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </a>
                    )}
                    {cta && (
                        <a
                            className="fb-cta"
                            href={cta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={brandColor ? { background: brandColor } : undefined}
                            onClick={() => recordClick && recordClick({ text: 'CTA: ' + cta.label, href: cta.url })}
                        >
                            {cta.label}
                        </a>
                    )}
                </div>
                {numPages && (
                    <div className="fb-progress">
                        <div style={{ width: `${progressPct}%`, ...(brandColor ? { background: brandColor } : null) }} />
                    </div>
                )}
            </header>

            <div className="fb-center" style={layout ? { transform: `translateY(${layout.offsetY}px)` } : undefined}>
                <Document
                    file={documentUrl}
                    onLoadError={onPdfLoadError(documentUrl)}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className="fb-loading"><div className="fb-spinner" /></div>}
                >
                    {layout && numPages && (
                        <div ref={zoomWrapRef} className="fb-zoom" style={{ width: stageW, height: H }}>
                        <div
                            ref={stageRef}
                            className={`fb-stage${isZoomed ? ' fb-stage-zoomed' : ''}`}
                            style={{ width: stageW, height: H, touchAction: pinched ? 'auto' : 'pinch-zoom' }}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerCancel={onPointerUp}
                            onLostPointerCapture={(ev) => {
                                if (FB_DEBUG && pressRef.current) console.log('[flipbook] drag ended: pointer capture lost', { x: Math.round(ev.clientX), y: Math.round(ev.clientY), buttons: ev.buttons }); // eslint-disable-line no-console
                                releaseRef.current(true);
                            }}
                            onPointerLeave={onPointerLeave}
                        >
                            <div ref={baseRef} className="fb-base" style={{ height: H }} />
                            {mounted.map((num) => (
                                <BookPage
                                    key={num}
                                    num={num}
                                    W={W}
                                    H={H}
                                    side={spread ? (num % 2 === 0 ? 'left' : 'right') : 'single'}
                                    hiDpr={hiDpr && (num === visLeft || num === visRight) ? hiDpr : null}
                                    hotspots={hotspotsByPage[num]}
                                    registerRef={registerRef}
                                    onGoToPage={goToPage}
                                />
                            ))}
                            <div ref={paperRef} className="fb-page fb-paper" style={{ width: W, height: H }} />
                            <div ref={underShadowRef} className="fb-shadow" style={{ zIndex: Z.underShadow }}><div /></div>
                            <div ref={frontShadeRef} className="fb-shadow" style={{ zIndex: Z.frontShade }}><div /></div>
                            <div ref={dropRef} className="fb-shadow fb-drop" style={{ zIndex: Z.drop }}><div /></div>
                            <div ref={backShadeRef} className="fb-shadow" style={{ zIndex: Z.backShade }}><div /></div>
                        </div>
                        </div>
                    )}
                </Document>
            </div>

            {numPages > 1 && (
                <div className="fb-nav" data-fb-chrome>
                    <NavButton onClick={() => turn('prev')} disabled={atStart} title="Previous (←)" path="M15.75 19.5 8.25 12l7.5-7.5" />
                    <div className="fb-nav-label">
                        {label}<span>/</span>{numPages}
                    </div>
                    <NavButton onClick={() => turn('next')} disabled={atEnd} title="Next (→)" path="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </div>
            )}

            {doShowBranding && (
                <a className="fb-branding" data-fb-chrome href={brandingLink} target="_blank" rel="nofollow noopener noreferrer">
                    {viewport.w < 640 ? brandName : `Shared with ${brandName}`}
                </a>
            )}
        </div>
    );
};

const Icon = ({ path, size = 18, stroke = 1.6 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={stroke} stroke="currentColor" width={size} height={size}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
);

const NavButton = ({ onClick, disabled, title, path }) => (
    <button type="button" className="fb-nav-btn" onClick={onClick} disabled={disabled} title={title}>
        <Icon path={path} size={14} stroke={2} />
    </button>
);

const IconButton = ({ onClick, title, path, disabled }) => (
    <button type="button" className="fb-icon" onClick={onClick} title={title} disabled={disabled}>
        <Icon path={path} />
    </button>
);

export default Book;
